import * as React from 'react';
import { Trash2, AlertTriangle } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  /** What the action is about (e.g. the licences a bulk action covers), shown under the body. */
  details?: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /**
   * The confirmed action is in flight (DF-39). The dialog stays open, both buttons are
   * disabled (no double submit) and Escape cannot dismiss it until the request settles.
   * Callers that keep the dialog open across an `await` should pass it; a handler that
   * returns a promise is also tracked here (see `onConfirm`), so forgetting it is not a
   * double-submit.
   */
  busy?: boolean;
  /**
   * A handler that returns a promise keeps the dialog busy until it settles, so a caller that
   * has not threaded its own `busy` through still cannot be double-submitted.
   */
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
}

// Per design spec (04-global-components.html §6 + README §4f).
// danger -> red confirm + Trash2 in red tint box; otherwise amber + AlertTriangle.
// Wraps ui/alert-dialog (Radix). Controlled by the `open` prop.
export function ConfirmDialog({
  open,
  title,
  body,
  details,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const IconCmp = danger ? Trash2 : AlertTriangle;
  // Radix fires onOpenChange(false) for the Cancel button, Escape and overlay dismissal, and
  // also for the Action button. Only the first group is "dismissed without confirming", so the
  // Action click below calls preventDefault(): Radix then reports nothing for a confirm and the
  // parent decides when to close. (This used to be a "confirmed" flag set on click and cleared by
  // the close callback; with an async onConfirm the callback hit the in-flight guard first, the
  // flag was never cleared, and the first Cancel or Escape of the next opening was swallowed.)
  // An async onConfirm marks the dialog busy by itself until the promise settles.
  const submittingRef = React.useRef(false);
  const [submitting, setSubmitting] = React.useState(false);
  const working = busy || submitting;
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        // In flight: nothing dismisses the dialog (Escape is also stopped below).
        if (working || submittingRef.current) return;
        onCancel();
      }}
    >
      <AlertDialogContent
        style={{ maxWidth: 440, borderRadius: 'var(--radius-xl)' }}
        onEscapeKeyDown={(e) => { if (working) e.preventDefault(); }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: danger ? 'var(--ps-pill-red-bg)' : 'var(--ps-pill-amber-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconCmp
              size={18}
              color={danger ? 'var(--status-expired)' : 'var(--ps-pill-amber-tx)'}
              data-testid="confirm-dialog-icon"
            />
          </div>
          <div>
            <AlertDialogTitle style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription
              style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}
            >
              {body}
            </AlertDialogDescription>
          </div>
        </div>
        {details}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={working}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={working}
            onClick={(e) => {
              // Radix's own close-on-action never runs, for a first click or a second one: the
              // parent decides when we close, and a confirm is never reported as a cancel.
              e.preventDefault();
              // A second click while the first is in flight must not submit again.
              if (working || submittingRef.current) return;
              const result = onConfirm();
              if (result && typeof (result as Promise<unknown>).then === 'function') {
                submittingRef.current = true;
                setSubmitting(true);
                void Promise.resolve(result).finally(() => {
                  submittingRef.current = false;
                  setSubmitting(false);
                });
              }
            }}
            style={
              danger
                ? { background: 'var(--status-expired)', color: '#fff' }
                : undefined
            }
          >
            {working ? 'Working…' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ConfirmDialog;
